"use client";

import { useState, useEffect, use } from "react";
import { useParams } from "react-router-dom";
import { Link } from "@/src/lib/next-compat";
import { ChevronLeft, Sparkles } from "lucide-react";
import { normalizeGeneratedText, normalizeGeneratedValue } from "@/lib/normalize-generated-text";
import {
    getReviewLessonQuizApiV1AdminReviewLessonItemUuidQuizGet,
    queueReviewQuizGenerationApiV1AdminReviewLessonItemUuidGenerateQuizPost,
} from "@/src/api/sdk.gen";

export default function LessonQuizPage({ params }: { params?: Promise<{ id: string }> } = {}) {
    const routeParams = useParams<{ id: string }>();
    const id = routeParams.id || "";
    const [data, setData] = useState<{
        hasQuiz: boolean;
        questions?: {
            question_text: string;
            options: unknown[];
            correct_answer?: string | null;
            order_index?: number;
        }[];
        lessonTitle?: string | null;
        lessonType?: string | null;
        lessonStatus?: string | null;
        title?: string;
        message?: string;
    } | null>(null);
    const [loading, setLoading] = useState(true);
    const [generateLoading, setGenerateLoading] = useState(false);
    const [polling, setPolling] = useState(false);

    const fetchQuiz = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const { data: resData, error } = await getReviewLessonQuizApiV1AdminReviewLessonItemUuidQuizGet({
                path: { item_uuid: id },
            });
            if (error || !resData) throw new Error("Failed to fetch quiz");
            setData(normalizeGeneratedValue(resData as any));
        } catch (err) {
            console.error(err);
            setData({ hasQuiz: false, message: "Failed to load quiz." });
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuiz();
    }, [id]);

    // Stop polling when quiz is ready
    useEffect(() => {
        if (polling && data?.hasQuiz && data.questions && data.questions.length > 0) {
            setPolling(false);
        }
    }, [polling, data?.hasQuiz, data?.questions?.length]);

    // Poll every 2.5s while waiting for quiz after generate
    useEffect(() => {
        if (!polling || !id) return;
        const interval = setInterval(() => fetchQuiz(true), 2500);
        return () => clearInterval(interval);
    }, [polling, id]);

    const handleGenerateQuiz = async () => {
        setGenerateLoading(true);
        try {
            const { error } = await queueReviewQuizGenerationApiV1AdminReviewLessonItemUuidGenerateQuizPost({
                path: { item_uuid: id },
            });
            if (error) throw new Error("Failed to start generation");
            setData(null);
            setPolling(true);
            fetchQuiz(); // initial check after starting generation
        } catch (err) {
            console.error(err);
        } finally {
            setGenerateLoading(false);
        }
    };
    const lessonApproved = data?.lessonStatus === "APPROVED";
    const lessonTypeLabel = data?.lessonType
        ? data.lessonType
              .trim()
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase())
        : null;

    return (
        <div className="h-[calc(100vh-theme(spacing.24))] flex flex-col relative">
            <div className="flex items-center justify-between mb-6 flex-shrink-0 sticky top-0 z-30 bg-card border-b border-border -mx-8 px-8 py-4 shadow-sm transition-all">
                <div className="flex items-center gap-4">
                    <Link
                        to={`/admin/reviews/lesson/${id}`}
                        className="p-2 rounded-full bg-muted text-muted-foreground hover:text-foreground hover:bg-secondary ring-1 ring-border transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </Link>
                    <div>
                        <div className="flex items-center gap-2 text-muted-foreground mb-0.5">
                            <span className="uppercase tracking-wide font-bold text-xs">QUIZ</span>
                            <span>•</span>
                            {lessonTypeLabel && (
                                <span
                                    className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-md bg-muted text-muted-foreground border border-border"
                                    title="Lesson type"
                                >
                                    {lessonTypeLabel}
                                </span>
                            )}
                        </div>
                        <h1 className="text-2xl font-bold text-foreground">
                            {data?.lessonTitle || "Lesson quiz"}
                        </h1>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            View and generate quiz for this lesson
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleGenerateQuiz}
                    disabled={generateLoading || !lessonApproved}
                    title={!lessonApproved ? "Only approved lessons can generate quizzes" : undefined}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {generateLoading ? (
                        <span className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                        <Sparkles className="w-5 h-5" />
                    )}
                    Generate quiz
                </button>
            </div>

            <div className="pt-2 max-w-5xl mx-auto w-full px-2">
                {!loading && data && !lessonApproved && (
                    <p className="text-xs text-amber-700 mb-4">
                        Quiz generation is disabled until this lesson is approved.
                    </p>
                )}

                <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    {loading ? (
                        <p className="text-slate-500">Loading quiz…</p>
                    ) : data?.hasQuiz && data.questions && data.questions.length > 0 ? (
                        <div className="space-y-6">
                            {data.title && (
                                <h2 className="text-lg font-semibold text-slate-900">{data.title}</h2>
                            )}
                            {data.questions
                                .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
                                .map((q, idx) => (
                                    <div key={idx} className="rounded-lg border border-slate-100 bg-slate-50/50 p-4">
                                        <p className="font-medium text-slate-900 mb-2">
                                            {idx + 1}. {normalizeGeneratedText(q.question_text)}
                                        </p>
                                        {Array.isArray(q.options) && (
                                            <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                                                {q.options.map((opt: unknown, oi: number) => {
                                                    const text =
                                                        typeof opt === "object" && opt !== null && "text" in (opt as object)
                                                            ? normalizeGeneratedText((opt as { text: string }).text)
                                                            : normalizeGeneratedText(String(opt));
                                                    const isCorrect =
                                                        q.correct_answer != null &&
                                                        (normalizeGeneratedText(String(opt)) === normalizeGeneratedText(String(q.correct_answer)) ||
                                                            (typeof opt === "object" &&
                                                                opt !== null &&
                                                                "text" in (opt as object) &&
                                                                normalizeGeneratedText((opt as { text: string }).text) === normalizeGeneratedText(String(q.correct_answer))));
                                                    return (
                                                        <li
                                                            key={oi}
                                                            className={isCorrect ? "text-emerald-700 font-medium" : ""}
                                                        >
                                                            {text}
                                                            {isCorrect && " ✓"}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <>
                            <p className="text-slate-600">
                                {data?.message ??
                                    "No questions generated for this lesson yet. Use “Generate quiz” to create the quiz."}
                            </p>
                            {polling && (
                                <p className="text-sm text-muted-foreground mt-3 flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                                    Generating quiz… Refreshing every few seconds.
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
